import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PRODUCTS, CATEGORIES } from "@/data/anabiya";
import { useEffectiveProducts, useEffectiveCategories, resolveImage } from "@/context/PricesContext";

/**
 * CompleteTheSet — internal-linking strip shown on PDPs.
 *
 * Rules (per the SEO plan):
 * - Links ONLY to categories that exist in the merged catalogue AND have at
 *   least one live product — never to an empty or non-existent category.
 * - Companion mapping is bedding-first (the "set" concept) with a few
 *   home-textile cross-links. No claims are invented; labels come straight
 *   from the category records.
 */
const SET_MAP = {
  "duvet-covers": ["sheet-sets", "pillow-covers", "quilts-bedspreads", "bedskirts"],
  "duvet-sets": ["sheet-sets", "pillow-covers", "quilts-bedspreads"],
  "sheet-sets": ["duvet-covers", "pillow-covers", "quilts-bedspreads", "bedskirts"],
  "quilts-bedspreads": ["sheet-sets", "duvet-covers", "pillow-covers"],
  "comforters": ["sheet-sets", "duvet-covers", "pillow-covers"],
  "pillow-covers": ["duvet-covers", "sheet-sets", "cushion-covers"],
  "monogram-bedding": ["sheet-sets", "duvet-covers", "pillow-covers"],
  "custom-bedding": ["duvet-covers", "sheet-sets", "pillow-covers"],
  "hotel-bedding": ["sheet-sets", "duvet-covers", "pillow-covers"],
  "bedskirts": ["duvet-covers", "sheet-sets", "quilts-bedspreads"],
  "bed-skirts": ["duvet-covers", "sheet-sets", "quilts-bedspreads"],
  "cushion-covers": ["pillow-covers", "throws-blankets", "curtains"],
  "throws-blankets": ["cushion-covers", "quilts-bedspreads", "curtains"],
};

export const CompleteTheSet = ({ category }) => {
  const all = useEffectiveProducts(PRODUCTS);
  const cats = useEffectiveCategories(CATEGORIES);

  const links = useMemo(() => {
    const wanted = SET_MAP[category] || [];
    if (!wanted.length) return [];
    const bySlug = new Map(cats.map((c) => [c.slug, c]));
    const hasProducts = new Set(all.map((p) => p.category));
    return wanted
      .filter((s) => bySlug.has(s) && hasProducts.has(s))
      .slice(0, 3)
      .map((s) => bySlug.get(s));
  }, [category, cats, all]);

  if (!links.length) return null;

  return (
    <section
      data-testid="complete-the-set"
      aria-label="Complete the set"
      className="px-6 md:px-12 lg:px-16 mt-16 md:mt-24"
    >
      <div className="border border-hairline bg-bone/60 px-6 py-8 md:px-10 md:py-10">
        <div className="overline text-gold mb-3">§ Complete the set</div>
        <h2 className="font-display text-forest text-2xl md:text-3xl leading-tight tracking-tight mb-7">
          Pairs beautifully <span className="font-editorial italic text-champagne">with these.</span>
        </h2>
        <div className="grid grid-cols-3 gap-3 md:gap-6">
          {links.map((c) => (
            <Link
              key={c.slug}
              to={`/shop?category=${c.slug}`}
              data-testid={`complete-set-link-${c.slug}`}
              className="group block min-w-0"
            >
              <div className="relative aspect-[4/3] overflow-hidden clip-slab bg-beige">
                <img
                  src={resolveImage(c.image)}
                  alt={`${c.label} — Anabiya Decor collection`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.05]"
                />
              </div>
              <div className="mt-3 flex items-center gap-2 text-forest group-hover:text-gold transition-colors duration-500">
                <span className="font-display text-sm md:text-lg leading-snug truncate">{c.label}</span>
                <ArrowRight size={14} strokeWidth={1.6} className="shrink-0 transition-transform duration-500 group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
